import UploadStatusIcon from '@components/Icons/UploadStatusIcon';
import { frostedGlassTransitionProps } from '@lib/utils/frostedPopoverTransition';
import { Popover } from '@mui/material';
import { notificationStore } from '@stores/Notifications';
import { observer } from 'mobx-react';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { UploadPanelBody } from './UploadPanelBody';
import css from './UploadStatus.module.scss';
import { useUploadActivity } from './useUploadActivity';

interface UploadStatusProps {
  /** Render slot for debug-only simulated `<BatchCard>` mounts (ToastDebugPage). */
  simulationCards?: React.ReactNode;
}

/**
 * Desktop upload-activity surface: a header trigger button + a popover hosting
 * the shared `<UploadPanelBody>`. The controller logic (results / delete-batch
 * modals + handlers) lives in `useUploadActivity`; its modals are rendered
 * OUTSIDE the popover so they survive the popover closing. The mobile
 * equivalent reuses the same hook + body inside a drawer (see MobileNotifCenter).
 */
export const UploadStatus = observer(({ simulationCards }: UploadStatusProps = {}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const { handlers, modals } = useUploadActivity();

  // Allow the store to programmatically open the popover (e.g. when an upload starts)
  const openPopover = useCallback(() => {
    if (buttonRef.current && !anchorEl) {
      setAnchorEl(buttonRef.current);
    }
  }, [anchorEl]);

  useEffect(() => {
    if (notificationStore.popoverOpen && !anchorEl && buttonRef.current) {
      openPopover();
    }
    // `notificationStore.popoverOpen` MUST stay in deps — the store flips it true
    // to auto-open the panel on upload start / rich events; without it the effect
    // never re-runs and the popover doesn't auto-open.
  }, [notificationStore.popoverOpen, anchorEl, openPopover]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    notificationStore.setPopoverOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    notificationStore.setPopoverOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        className={css.uploadStatus}
        aria-label="Upload status"
        onClick={handleClick}
      >
        <UploadStatusIcon />
        {notificationStore.unreadCount > 0 && (
          <span className={css.badge}>{notificationStore.unreadCount}</span>
        )}
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          transition: frostedGlassTransitionProps,
          paper: {
            sx: {
              background: 'rgba(30, 30, 30, 0.85)',
              backdropFilter: 'blur(50px)',
              WebkitBackdropFilter: 'blur(50px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              boxShadow:
                '0 24px 32px -12px rgba(18, 18, 18, 0.6), 0 8px 24px -8px rgba(0, 0, 0, 0.4)',
              marginTop: '8px',
              overflow: 'hidden',
            },
          },
        }}
      >
        <UploadPanelBody handlers={handlers} simulationCards={simulationCards} />
      </Popover>
      {modals}
    </>
  );
});
